import React, { useState, useEffect } from 'react';
import { Search, Filter, Plus, Settings, AlertCircle, Database, RefreshCw, ChevronLeft, ChevronRight, Users, Copy, Bot, FileEdit, X } from 'lucide-react';
import { ProductService, Product } from '../services/productService';
import AddProductModal from '../components/AddProductModal';
import ChatProductWizard, { RascunhoProduto } from '../components/ChatProductWizard';
import ConcorrentesModal from '../components/ConcorrentesModal';
import apiClient from '../services/api/client';
import clsx from 'clsx';
import BlackFridayPriceHint, { temMercadoLivre } from '../components/BlackFridayPriceHint';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://qflow.xflow.ca:5005';

/** Converte URLs relativas de media para URL absoluta do servidor Python */
const resolveMediaUrl = (url: string): string => {
  if (!url) return '';
  if (url.startsWith('http')) return url;               // já absoluta
  if (url.startsWith('/site/api/hub/products/media'))   // legacy relativa
    return `${API_BASE}${url}`;
  return url;
};

export default function Catalog() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estados para controle do Modal de Edição/Criação
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(undefined);
  const [isCopy, setIsCopy] = useState(false);
  // Novo produto: escolha entre manual e via chat
  const [escolhaNovo, setEscolhaNovo] = useState(false);
  const [chatAberto, setChatAberto] = useState(false);
  const [rascunho, setRascunho] = useState<{ dados: RascunhoProduto; mps: string[] } | null>(null);
  // Produto aberto no modal de concorrentes (mesmo produto em outros vendedores)
  const [concorrentesDe, setConcorrentesDe] = useState<Product | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Paginação (feita no servidor)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState<'recent' | 'sku' | 'title'>('recent');
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // NOVO: Estado para armazenar os dados do Storage
  const [storageStats, setStorageStats] = useState({ usedGB: 0, percentage: 0 });

  useEffect(() => {
    loadStorageStats(); // Carrega os dados de armazenamento
  }, []);

  // Busca com atraso de 400 ms — pesquisa em TODO o catálogo (no servidor), não só na página atual
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchTerm.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    loadProducts();
    document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sort, debouncedSearch]);

  const handleSyncStock = async (product: Product) => {
    try {
      await apiClient.post(`/products/${product._id || product.id}/sync-stock`);
      alert(`Aviso enviado! O stock do SKU ${product.sku} está a ser replicado para todos os canais.`);
    } catch (e) {
      alert('Erro ao tentar sincronizar o stock.');
    }
  };

  const loadStorageStats = async () => {
    try {
      const { data } = await apiClient.get('/storage/stats');
      setStorageStats({
        usedGB: data.usedGB || 0,
        percentage: data.percentage || 0,
      });
    } catch (err) {
      console.error('Erro ao carregar status do storage:', err);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ProductService.getProducts({
        page, limit: pageSize, sort, search: debouncedSearch || undefined,
      });

      if (data && Array.isArray(data.products)) {
        setProducts(data.products);
        const tot = data.pagination?.total ?? data.products.length;
        const pages = Math.max(1, data.pagination?.totalPages ?? 1);
        setTotal(tot);
        setTotalPages(pages);
        // Se a página atual ficou além do fim (ex.: após excluir), volta para a última
        if (page > pages) setPage(pages);
      } else {
        setProducts([]);
        setTotal(0);
        setTotalPages(1);
      }
    } catch (err: any) {
      console.error('Erro ao carregar catálogo:', err);
      setError(err.message || 'Erro ao carregar catálogo');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = () => setEscolhaNovo(true);

  const abrirManual = () => {
    setEscolhaNovo(false);
    setSelectedProduct(undefined);
    setIsCopy(false);
    setRascunho(null);
    setIsModalOpen(true);
  };

  const abrirChat = () => { setEscolhaNovo(false); setChatAberto(true); };

  // Do chat para o formulário completo, já preenchido
  const chatParaFormulario = (dados: RascunhoProduto, mps: string[]) => {
    setChatAberto(false);
    setSelectedProduct(undefined);
    setIsCopy(false);
    setRascunho({ dados, mps });
    setIsModalOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    setRascunho(null);
    setIsCopy(false);
    setIsModalOpen(true);
  };

  // Duplicar: abre o cadastro com os dados do produto para criar outro (ex.: outro tamanho)
  const handleCopyProduct = (product: Product) => {
    setSelectedProduct(product);
    setRascunho(null);
    setIsCopy(true);
    setIsModalOpen(true);
  };

  // Callback ao salvar/editar produto (recarrega os produtos e o storage)
  const handleSuccess = () => {
    loadProducts();
    loadStorageStats();
  };

  // A busca já é feita no servidor (em todo o catálogo)
  const filteredProducts = products;
  const firstItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastItem  = Math.min(page * pageSize, total);

  // Barra de paginação — aparece no topo e no rodapé da lista
  const goTo = (p: number) => setPage(Math.min(Math.max(1, p), totalPages));
  const pageNumbers = (() => {
    const nums: (number | '…')[] = [];
    const add = (n: number) => { if (!nums.includes(n)) nums.push(n); };
    add(1);
    for (let n = page - 1; n <= page + 1; n++) if (n > 1 && n < totalPages) add(n);
    if (totalPages > 1) add(totalPages);
    const out: (number | '…')[] = [];
    nums.sort((a, b) => (a as number) - (b as number)).forEach((n, i, arr) => {
      if (i > 0 && (n as number) - (arr[i - 1] as number) > 1) out.push('…');
      out.push(n);
    });
    return out;
  })();

  const paginationBar = (
      <div className="px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
        <div className="text-dark-400">
          Mostrando <span className="text-dark-100 font-medium">{firstItem}–{lastItem}</span> de{' '}
          <span className="text-dark-100 font-medium">{total}</span> produto{total === 1 ? '' : 's'}
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-center">
          <label className="flex items-center gap-2 text-dark-400">
            Por página
            <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="bg-dark-800 border border-dark-700 rounded-lg px-2 py-1 text-dark-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {[20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <div className="flex items-center gap-1">
            <button
                onClick={() => goTo(page - 1)}
                disabled={page <= 1}
                className="p-1.5 rounded-lg border border-dark-700 text-dark-300 hover:bg-dark-800 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Página anterior"
            >
              <ChevronLeft size={18} />
            </button>
            {pageNumbers.map((n, i) => n === '…' ? (
                <span key={`e${i}`} className="px-1 text-dark-500">…</span>
            ) : (
                <button
                    key={n}
                    onClick={() => goTo(n)}
                    className={clsx(
                        "min-w-[32px] h-8 px-2 rounded-lg border text-sm transition-colors",
                        n === page
                            ? "bg-primary-500 border-primary-500 text-white font-semibold"
                            : "border-dark-700 text-dark-300 hover:bg-dark-800"
                    )}
                >
                  {n}
                </button>
            ))}
            <button
                onClick={() => goTo(page + 1)}
                disabled={page >= totalPages}
                className="p-1.5 rounded-lg border border-dark-700 text-dark-300 hover:bg-dark-800 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Próxima página"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
  );

  return (
      <div className="p-4 sm:p-6 md:p-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-dark-100 mb-1">Catálogo de Produtos</h1>
            <p className="text-dark-400 text-sm">Gerencie seus produtos e integrações</p>
          </div>

          <div className="flex flex-wrap items-center gap-4 sm:gap-6">

            {/* Medidor de Armazenamento */}
            <div className="hidden md:flex flex-col items-end">
              <div className="flex justify-between w-full mb-1 items-center gap-3">
                <span className="text-xs text-dark-400 flex items-center gap-1">
                  <Database size={12} /> Plano Inicial (5GB)
                </span>
                <span className="text-xs font-medium text-dark-100">
                  {storageStats.usedGB} GB
                </span>
              </div>
              <div className="w-48 h-2 bg-dark-800 rounded-full overflow-hidden border border-dark-700">
                <div
                    className={clsx(
                        "h-full rounded-full transition-all duration-1000",
                        storageStats.percentage > 90 ? "bg-red-500" :
                            storageStats.percentage > 70 ? "bg-yellow-500" : "bg-primary-500"
                    )}
                    style={{ width: `${Math.min(storageStats.percentage, 100)}%` }}
                    title={`${storageStats.percentage}% utilizado`}
                />
              </div>
            </div>

            <button
                onClick={handleAddProduct}
                className="bg-primary-500 hover:bg-primary-600 text-dark-100 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-lg shadow-primary-500/20 whitespace-nowrap"
            >
              <Plus size={20} />
              Novo Produto
            </button>
          </div>
        </div>

        <div className="bg-dark-900 rounded-xl border border-dark-700 overflow-hidden">
          {/* Barra de Filtros */}
          <div className="p-3 sm:p-4 border-b border-dark-700 flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-500" size={20} />
              <input
                  type="text"
                  placeholder="Buscar por título, SKU ou descrição em todo o catálogo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-dark-800 border border-dark-700 rounded-lg pl-10 pr-4 py-2 text-dark-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <select
                value={sort}
                onChange={(e) => { setSort(e.target.value as any); setPage(1); }}
                className="px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-dark-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                title="Ordenar por"
            >
              <option value="recent">Mais recentes</option>
              <option value="sku">SKU</option>
              <option value="title">Nome</option>
            </select>
            <button
                onClick={loadProducts}
                className="px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-dark-100 flex items-center justify-center gap-2 hover:bg-dark-700 transition-colors whitespace-nowrap"
            >
              <Filter size={20} />
              Atualizar
            </button>
          </div>

          {/* Paginação (topo) — visível sem precisar rolar */}
          {!loading && !error && total > 0 && (
              <div className="border-b border-dark-700 bg-dark-800/20">{paginationBar}</div>
          )}

          {/* Listagem */}
          {loading ? (
              <div className="p-12 text-center">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-dark-400">Carregando catálogo...</p>
              </div>
          ) : error ? (
              <div className="p-12 text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="text-dark-100 mb-4">{error}</p>
                <button onClick={loadProducts} className="text-primary-500 hover:underline">Tentar novamente</button>
              </div>
          ) : filteredProducts.length === 0 ? (
              <div className="p-12 text-center text-dark-400">
                Nenhum produto encontrado.
              </div>
          ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] border-collapse">
                  <thead>
                  <tr className="text-left border-b border-dark-700 bg-dark-800/30">
                    <th className="p-4 font-medium text-dark-400">Produto</th>
                    <th className="p-4 font-medium text-dark-400">SKU</th>
                    <th className="p-4 font-medium text-dark-400">Preço</th>
                    <th className="p-4 font-medium text-dark-400">Estoque</th>
                    <th className="p-4"></th>
                  </tr>
                  </thead>
                  <tbody>
                  {filteredProducts.map((product) => (
                      <React.Fragment key={product.id || product._id || Math.random()}>
                        {/* Linha Principal */}
                        <tr className="border-t border-dark-700/50 hover:bg-dark-800/30 transition-colors group">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <img
                                  src={resolveMediaUrl(product.image || (product.images && product.images[0]) || '')}
                                  alt={product.title}
                                  className="w-12 h-12 rounded-lg object-cover bg-dark-800"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                    if (placeholder) placeholder.style.display = 'flex';
                                  }}
                              />
                              <div
                                  className="w-12 h-12 rounded-lg bg-dark-800 items-center justify-center text-dark-400"
                                  style={{ display: (product.image || (product.images && product.images[0])) ? 'none' : 'flex' }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                                </svg>
                              </div>
                              <div>
                                <p className="font-bold text-dark-100 text-lg line-clamp-1">{product.title}</p>
                                <p className="text-xs text-dark-400 uppercase">{product.category || 'Sem Categoria'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-dark-200 font-mono font-medium">{product.sku}</td>
                          <td className="p-4 text-dark-100 font-semibold">
                            <span className="inline-flex items-center gap-1.5">
                              R$ {(product.basePrice || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              {temMercadoLivre(product.channels?.map(c => c.marketplaceId)) && <BlackFridayPriceHint />}
                            </span>
                            {(() => {
                              // Preços próprios por marketplace (campo <mp>_price); vazio = preço base
                              const proprios = (product.channels || [])
                                .map(c => ({ nome: c.marketplaceName || c.marketplaceId, v: Number((product as any)[`${c.marketplaceId}_price`]) }))
                                .filter(x => x.v > 0 && x.v !== Number(product.basePrice));
                              return proprios.length > 0 ? (
                                <div className="mt-1 space-y-0.5">
                                  {proprios.map(x => (
                                    <p key={x.nome} className="text-[11px] font-normal text-dark-400">
                                      {x.nome}: R$ {x.v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </p>
                                  ))}
                                </div>
                              ) : null;
                            })()}
                          </td>
                          <td className="p-4 text-dark-100">{product.stock || 0}</td>
                          <td className="p-4 text-right flex justify-end gap-2">
                            <button
                                onClick={() => handleSyncStock(product)}
                                className="p-2 hover:bg-dark-700 rounded-lg text-primary-400 hover:text-primary-300 transition-colors"
                                title="Forçar Sincronização de Stock"
                            >
                              <RefreshCw size={20} />
                            </button>
                            <button
                                onClick={() => setConcorrentesDe(product)}
                                className="p-2 hover:bg-dark-700 rounded-lg text-amber-400 hover:text-amber-300 transition-colors"
                                title="Ver concorrentes (mesmo produto em outros vendedores)"
                            >
                              <Users size={20} />
                            </button>
                            <button
                                onClick={() => handleCopyProduct(product)}
                                className="p-2 hover:bg-dark-700 rounded-lg text-sky-600 hover:text-sky-700 transition-colors"
                                title="Duplicar produto (ex.: criar outro tamanho ou cor)"
                            >
                              <Copy size={20} />
                            </button>
                            <button
                                onClick={() => handleEditProduct(product)}
                                className="p-2 hover:bg-dark-700 rounded-lg text-dark-400 hover:text-dark-100 transition-colors"
                                title="Editar Produto"
                            >
                              <Settings size={20} />
                            </button>
                          </td>
                        </tr>
                        {/* Linha de Marketplaces */}
                        <tr className="border-b border-dark-700 group-hover:bg-dark-800/30 transition-colors">
                          <td colSpan={5} className="px-4 pb-4 pt-0">
                            <div className="flex flex-wrap gap-2 items-center pl-[60px]">
                              {product.channels && product.channels.length > 0 ? (
                                  product.channels.map((channel, idx) => {
                                      const cls = `text-xs px-2 py-1 rounded border ${
                                          channel.status === 'synced' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' :
                                              channel.status === 'error' ? 'border-red-300 text-red-700 bg-red-50' :
                                                  'border-dark-600 text-dark-400 bg-dark-800'
                                      }`;
                                      return channel.status === 'synced' && channel.url ? (
                                          <a
                                              key={`${channel.marketplaceId}-${idx}`}
                                              href={channel.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              title={(channel as any).warnings?.[0]?.message
                                                  ? `${(channel as any).warnings[0].message}\n\nClique para abrir o anúncio no ${channel.marketplaceName}`
                                                  : `Abrir anúncio no ${channel.marketplaceName}`}
                                              className={`${cls} hover:underline inline-flex items-center gap-1`}
                                          >
                                            {(channel as any).warnings?.length > 0 && <span className="text-amber-600">⚠</span>}
                                            {channel.marketplaceName} ↗
                                          </a>
                                      ) : (
                                          <span key={`${channel.marketplaceId}-${idx}`} className={cls}
                                                title={channel.errors?.[0]?.message || ''}>
                                            {channel.marketplaceName}
                                          </span>
                                      );
                                  })
                              ) : (
                                  <span className="text-xs text-dark-600 italic">Sem integrações ativas</span>
                              )}
                            </div>
                            {/* Mensagens de erro/aviso de cada marketplace, visíveis sem passar o mouse */}
                            {(product.channels || []).some(c => (c.status === 'error' && c.errors?.length) || (c as any).warnings?.length) && (
                              <div className="mt-2 pl-[60px] space-y-1">
                                {(product.channels || []).map((c, i) => {
                                  const erro = c.status === 'error' ? c.errors?.[0]?.message : '';
                                  const aviso = !erro ? (c as any).warnings?.[0]?.message : '';
                                  if (!erro && !aviso) return null;
                                  return (
                                    <div key={`msg-${c.marketplaceId}-${i}`}
                                         className={`flex items-start gap-2 text-xs rounded px-2 py-1.5 border ${erro
                                           ? 'text-red-800 bg-red-50 border-red-300'
                                           : 'text-amber-900 bg-amber-50 border-amber-300'}`}>
                                      <span className={`shrink-0 font-bold ${erro ? 'text-red-600' : 'text-amber-600'}`}>{erro ? '✖' : '⚠'}</span>
                                      <span className="flex-1 whitespace-pre-line">
                                        <strong>{c.marketplaceName}:</strong> {String(erro || aviso).replace(/^\[[^\]]+\]\s*/, '')}
                                      </span>
                                      <button type="button" onClick={() => handleEditProduct(product)}
                                              className={`shrink-0 font-semibold underline hover:no-underline ${erro ? 'text-red-700' : 'text-amber-800'}`}>
                                        Corrigir
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                  ))}
                  </tbody>
                </table>
              </div>
          )}

          {/* Paginação (rodapé) */}
          {!loading && !error && total > 0 && (
              <div className="border-t border-dark-700">{paginationBar}</div>
          )}
        </div>

        <AddProductModal
            isOpen={isModalOpen}
            onClose={() => { setIsModalOpen(false); setRascunho(null); }}
            onSuccess={handleSuccess}
            product={selectedProduct}
            isCopy={isCopy}
            rascunho={rascunho?.dados || null}
            rascunhoMarketplaces={rascunho?.mps || []}
        />
        <ChatProductWizard
            isOpen={chatAberto}
            onClose={() => setChatAberto(false)}
            onSuccess={handleSuccess}
            onAbrirFormulario={chatParaFormulario}
        />
        {escolhaNovo && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setEscolhaNovo(false)}>
            <div className="bg-dark-950 rounded-xl border border-dark-700 w-full max-w-lg p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-xl font-bold text-dark-100">Novo produto</h2>
                <button onClick={() => setEscolhaNovo(false)} className="p-1.5 rounded-lg text-dark-400 hover:bg-dark-800"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-sm text-dark-400 mb-5">Como você quer cadastrar?</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button onClick={abrirChat}
                        className="text-left p-4 rounded-xl border-2 border-primary-500 bg-primary-500/10 hover:bg-primary-500/20 transition-colors">
                  <Bot className="w-7 h-7 text-primary-500 mb-2" />
                  <p className="font-semibold text-dark-100">Inserir via chat</p>
                  <p className="text-xs text-dark-400 mt-1">Envie a foto e a IA sugere título, descrição e categorias. Você só aprova.</p>
                </button>
                <button onClick={abrirManual}
                        className="text-left p-4 rounded-xl border-2 border-dark-700 hover:border-dark-600 hover:bg-dark-800 transition-colors">
                  <FileEdit className="w-7 h-7 text-dark-300 mb-2" />
                  <p className="font-semibold text-dark-100">Inserir manual</p>
                  <p className="text-xs text-dark-400 mt-1">O formulário completo, campo a campo, como antes.</p>
                </button>
              </div>
            </div>
          </div>
        )}
        <ConcorrentesModal product={concorrentesDe} onClose={() => setConcorrentesDe(null)} />
      </div>
  );
}
